import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpRequest } from "@/lib/axios";
import { CreateDealRequestDTO, UpdateDealRequestDTO, DealResponse } from "@/types";

async function createDeal(
  request: CreateDealRequestDTO
): Promise<DealResponse> {
  const response = await httpRequest.post<DealResponse>(
    "/clinic/crm/deals",
    request
  );
  return response.data;
}

export function useCreateDealMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDeal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals", "pipeline"] });
    },
  });
}

async function updateDeal(
  id: string,
  request: UpdateDealRequestDTO
): Promise<DealResponse> {
  const response = await httpRequest.put<DealResponse>(
    `/clinic/crm/deals/${id}`,
    request
  );
  return response.data;
}

export function useUpdateDealMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDealRequestDTO }) =>
      updateDeal(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals", "pipeline"] });
      queryClient.invalidateQueries({ queryKey: ["deals", id] });
    },
  });
}

async function moveDealStage(
  id: string,
  stageId: string
): Promise<DealResponse> {
  const response = await httpRequest.post<DealResponse>(
    `/clinic/crm/deals/${id}/move`,
    { stage_id: stageId }
  );
  return response.data;
}

export function useMoveDealStageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) =>
      moveDealStage(id, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals", "pipeline"] });
    },
  });
}

async function deleteDeal(id: string): Promise<void> {
  await httpRequest.delete(`/clinic/crm/deals/${id}`);
}

export function useDeleteDealMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDeal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals", "pipeline"] });
    },
  });
}